import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { BsTwitter, BsYoutube } from "react-icons/bs";
import { FaFacebookF } from "react-icons/fa";
import { SiLinkedin } from "react-icons/si";
import {
  Mail,
  Phone,
  MapPin,
  Sparkles,
  ArrowUpRight,
  Building2,
  GraduationCap,
  ShoppingBag,
  ArrowRight,
  Zap,
  Layers,
} from "lucide-react";
import { KRUMAGO } from "../../s3-assets/Krumago";
import { useNavigate } from "react-router-dom";
import { academyLogger } from "utils/logger";

const isMainPage = (pathname) => pathname === "/";

const KrumagoFooter = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const companyState =
    useSelector((state) => state?.companyReducer?.company) || {};

  const [hoveredSocial, setHoveredSocial] = useState(null);
  const [hoveredLink, setHoveredLink] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  const footerRef = useRef(null);

  const onMainPage = isMainPage(location.pathname);

  // Get legal documents from company
  const legalDocuments = companyState?.legalDocuments;

  // Format contentSubType for display
  const formatContentSubType = (contentSubType) => {
    if (!contentSubType) return "Legal Document";
    return contentSubType
      .toLowerCase()
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Handle policy link click
  const handlePolicyClick = (doc, event) => {
    event.preventDefault();

    academyLogger.info("User clicked legal document:", doc);
    academyLogger.info("Document Name:", doc.name);
    academyLogger.info("Content SubType:", doc.contentSubType);
    academyLogger.info("Display Link:", doc.displayLink);

    if (doc.contentSubType) {
      const policyName = doc.contentSubType.toLowerCase();
      navigate(`/policy/${policyName}`);
    }
  };

  // Transform legal documents for navigation
  const getLegalLinks = () => {
    if (legalDocuments?.length > 0) {
      return legalDocuments.map(doc => ({
        name: formatContentSubType(doc.contentSubType) || doc.name || "Legal Document",
        document: doc,
        href: "#" // placeholder, will use onClick
      }));
    }
    // Return empty array if no legal documents
    return [];
  };

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (footerRef.current) {
        const rect = footerRef.current.getBoundingClientRect();
        setMousePos({
          x: ((e.clientX - rect.left) / rect.width) * 100,
          y: ((e.clientY - rect.top) / rect.height) * 100,
        });
      }
    };
    const el = footerRef.current;
    if (el) el.addEventListener("mousemove", handleMouseMove);
    return () => {
      if (el) el.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  const formatSocialUrl = (url) => {
    if (!url || url === "#") return "#";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    if (url.includes(".com") || url.includes(".org") || url.includes(".net"))
      return "https://" + url;
    return "#";
  };

  const findSocialMedia = (name) => {
    if (
      !companyState.socialMediaAccounts ||
      !Array.isArray(companyState.socialMediaAccounts)
    )
      return null;
    const account = companyState.socialMediaAccounts.find(
      (s) =>
        s.name.toLowerCase() === name.toLowerCase() ||
        s.name.toLowerCase() === name.toLowerCase().replace(" ", "")
    );
    return account ? account.value : null;
  };

  const formatAddress = (address) => {
    if (!address) return "";
    const parts = [];
    if (address.streetName1) parts.push(address.streetName1);
    if (address.streetName2) parts.push(address.streetName2);
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    if (address.zipcode) parts.push(address.zipcode);
    if (address.country) parts.push(address.country);
    return parts.join(", ");
  };

  const formatPhone = (phone) => {
    if (!phone) return "";
    let cleaned = phone.replace(/\D/g, "");
    cleaned = cleaned.replace(/^0+/, "");
    if (cleaned.startsWith("91") && cleaned.length === 12) {
      const num = cleaned.substring(2);
      return "+91 " + num.slice(0, 5) + " " + num.slice(5);
    }
    if (cleaned.startsWith("1") && cleaned.length === 11)
      cleaned = cleaned.substring(1);
    if (cleaned.length === 10)
      return (
        "+1 (" +
        cleaned.slice(0, 3) +
        ") " +
        cleaned.slice(3, 6) +
        "-" +
        cleaned.slice(6)
      );
    return "+" + cleaned;
  };

  const socialLinks = [
    {
      icon: BsTwitter,
      label: "Twitter",
      url: formatSocialUrl(findSocialMedia("twitter")),
      color: "#1DA1F2",
      bg: "#e8f5fe",
      border: "#bae0fd",
    },
    {
      icon: SiLinkedin,
      label: "LinkedIn",
      url: formatSocialUrl(
        findSocialMedia("linkedin") || findSocialMedia("iinkedIn")
      ),
      color: "#0A66C2",
      bg: "#e8f0fb",
      border: "#b9d0f5",
    },
    {
      icon: BsYoutube,
      label: "YouTube",
      url: formatSocialUrl(findSocialMedia("youtube")),
      color: "#FF0000",
      bg: "#fee8e8",
      border: "#fdb8b8",
    },
    {
      icon: FaFacebookF,
      label: "Facebook",
      url: formatSocialUrl(findSocialMedia("facebook")),
      color: "#1877F2",
      bg: "#e8f0fe",
      border: "#b8d0fc",
    },
  ];

  const navSections = [
    {
      title: "Company",
      accent: "#8b5cf6",
      links: [{ name: "About Us", href: "/who-are-we" }],
    },
    {
      title: "Legal",
      accent: "#10b981",
      links: getLegalLinks(), // Use dynamic legal links here
    },
  ];

  const industries = [
    {
      icon: Building2,
      label: "FlexOffice",
      href: "/cowork-management-software",
      color: "#3b82f6",
      bg: "#eff6ff",
      border: "#bfdbfe",
    },
    {
      icon: GraduationCap,
      label: "Academy",
      href: "/academy-management-software",
      color: "#8b5cf6",
      bg: "#f5f3ff",
      border: "#ddd6fe",
    },
    {
      icon: ShoppingBag,
      label: "Retail",
      href: "/retail-management-software",
      color: "#10b981",
      bg: "#ecfdf5",
      border: "#a7f3d0",
    },
    {
      icon: Layers,
      label: "WaaS",
      href: "/waas-management-software",
      color: "#f59e0b",
      bg: "#fffbea",
      border: "#fcd34d",
    },
  ];

  const isMobile = windowWidth < 640;
  const isTablet = windowWidth >= 640 && windowWidth < 768;
  const isDesktop = windowWidth >= 1024;

  const defaultEmail = "system@avpstech.com";
  const defaultPhone = "+17042368409";
  const defaultPhoneDisplay = "+1 (704) 236-8409";
  const defaultAddress = "6110 McFarland Station Dr, Alpharetta, GA 30004";

  const contactEmail = companyState.contactPerson
    ? companyState.contactPerson.email || defaultEmail
    : defaultEmail;
  const contactPhone = companyState.contactPerson
    ? companyState.contactPerson.phoneNumber || defaultPhone
    : defaultPhone;
  const contactPhoneDisplay =
    formatPhone(contactPhone) || defaultPhoneDisplay;
  const contactAddress = formatAddress(companyState.address) || defaultAddress;

  return (
    <footer
      ref={footerRef}
      style={{
        position: "relative",
        overflow: "hidden",
        fontFamily: "'Inter', -apple-system, sans-serif",
      }}
    >
      <style>{`
        @keyframes gradShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes badgePulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(251,191,36,0.4); }
          50% { box-shadow: 0 0 0 6px rgba(251,191,36,0); }
        }
        .kf-social-btn { transition: all 0.22s cubic-bezier(0.34,1.56,0.64,1) !important; }
        .kf-social-btn:hover { transform: translateY(-5px) scale(1.15) !important; }
        .kf-ind-pill { transition: all 0.2s cubic-bezier(0.34,1.4,0.64,1) !important; }
        .kf-ind-pill:hover { transform: translateY(-3px) scale(1.05) !important; box-shadow: 0 6px 16px rgba(0,0,0,0.1) !important; }
        .kf-cicon { transition: all 0.22s cubic-bezier(0.34,1.4,0.64,1) !important; }
        .kf-cta-btn { transition: all 0.22s cubic-bezier(0.34,1.56,0.64,1) !important; }
        .kf-cta-btn:hover { transform: scale(1.06) translateY(-2px) !important; box-shadow: 0 14px 36px rgba(29,78,216,0.4) !important; }
        .kf-bot-link { transition: color 0.18s !important; text-decoration: none; }
        .kf-bot-link:hover { color: #1d4ed8 !important; }
        .kf-nav-link { transition: all 0.18s ease !important; }
        .kf-nav-link:hover { padding-left: 6px !important; }
        .kf-contact-item { transition: all 0.22s ease !important; border-radius: 12px; cursor: pointer; text-decoration: none; color: inherit; display: flex; }
        .kf-contact-item:hover { background-color: #f1f5f9 !important; border-radius: 16px !important; }
        @media (max-width: 640px) {
          .kf-contact-item { padding: 16px !important; }
          .kf-contact-item:hover { transform: translateY(-2px); }
        }
      `}</style>

      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex: 0,
          background:
            "radial-gradient(ellipse 35% 35% at " +
            mousePos.x +
            "% " +
            mousePos.y +
            "%, rgba(59,130,246,0.05), transparent)",
          transition: "background 0.4s ease",
        }}
      />

      {/* TOP CTA BANNER */}
      <div
        style={{
          background:
            "linear-gradient(135deg, #0f172a 0%, #1e3a8a 45%, #2563eb 100%)",
          backgroundSize: "200% 200%",
          animation: "gradShift 10s ease infinite",
          padding: isMobile ? "32px 20px" : isTablet ? "40px 32px" : "48px 40px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: -50,
            right: "6%",
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.07)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: -40,
            left: "3%",
            width: 140,
            height: 140,
            borderRadius: "50%",
            background: "rgba(239,68,68,0.07)",
            border: "1px solid rgba(239,68,68,0.1)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "flex-start" : "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: isMobile ? 20 : 28,
            position: "relative",
          }}
        >
          <div>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                background: "rgba(251,191,36,0.15)",
                border: "1px solid rgba(251,191,36,0.3)",
                color: "#fbbf24",
                fontSize: isMobile ? 10 : 11,
                fontWeight: 700,
                letterSpacing: "0.07em",
                padding: isMobile ? "4px 12px" : "5px 14px",
                borderRadius: 999,
                marginBottom: isMobile ? 10 : 14,
                animation: "badgePulse 2.5s ease infinite",
              }}
            >
              <Zap size={isMobile ? 10 : 11} />
              {" POWERING MODERN BUSINESSES"}
            </div>
            <h2
              style={{
                color: "white",
                fontSize: isMobile
                  ? "20px"
                  : isTablet
                    ? "24px"
                    : "clamp(20px,2.6vw,28px)",
                fontWeight: 800,
                margin: "0 0 8px",
                lineHeight: 1.25,
              }}
            >
              Ready to streamline your operations?
            </h2>
            <p
              style={{
                color: "rgba(255,255,255,0.55)",
                fontSize: isMobile ? "13px" : "14px",
                margin: 0,
              }}
            >
              Start for free · Easy to get started · Explore all features for 14 days
            </p>
          </div>

          <a
            href="/contact"
            className="kf-cta-btn"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              background: "white",
              color: "#1d4ed8",
              fontSize: "14px",
              fontWeight: 700,
              padding: isMobile ? "12px 24px" : "13px 26px",
              borderRadius: 12,
              textDecoration: "none",
              boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
              whiteSpace: isMobile ? "normal" : "nowrap",
              border: "none",
              width: isMobile ? "100%" : "auto",
            }}
          >
            <Sparkles size={isMobile ? 14 : 15} />
            {" Get Started Free "}
            <ArrowUpRight size={isMobile ? 14 : 15} />
          </a>
        </div>
      </div>

      {/* MAIN FOOTER BODY */}
      <div
        style={{
          background: "#f8fafc",
          borderTop: "1px solid #e2e8f0",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: isMobile
              ? "32px 20px 0"
              : isTablet
                ? "40px 32px 0"
                : "44px 40px 0",
          }}
        >
          {/* MAIN GRID */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr"
                : isTablet
                  ? "repeat(2, 1fr)"
                  : "1.6fr 1fr 1fr",
              gap: isMobile ? 32 : 36,
              paddingBottom: 36,
              borderBottom: "1.5px solid #e2e8f0",
              alignItems: "start",
            }}
          >
            {/* BRAND COLUMN */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 0,
              }}
            >
              <div
                style={{
                  display: "inline-block",
                  marginBottom: 14,
                  padding: isMobile ? "8px 14px" : "10px 16px",
                  background: "white",
                  borderRadius: 12,
                  border: "1.5px solid #e2e8f0",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
                  width: "fit-content",
                }}
              >
                <img
                  src={KRUMAGO.IMAGES.KRUMAGO_3}
                  alt="Krumago"
                  style={{
                    width: isMobile ? 110 : 130,
                    height: "auto",
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              </div>

              {onMainPage && (
                <>
                  <p
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#94a3b8",
                      letterSpacing: "0.09em",
                      margin: "0 0 8px",
                    }}
                  >
                    Our Solutions
                  </p>

                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 7,
                      marginBottom: 20,
                    }}
                  >
                    {industries.map((ind, i) => {
                      const Icon = ind.icon;
                      return (
                        <a
                          key={i}
                          href={ind.href}
                          className="kf-ind-pill"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: isMobile ? "5px 10px" : "6px 12px",
                            borderRadius: 9,
                            background: ind.bg,
                            border: "1.5px solid " + ind.border,
                            color: ind.color,
                            fontSize: isMobile ? "11px" : "12px",
                            fontWeight: 700,
                            textDecoration: "none",
                          }}
                        >
                          <Icon size={isMobile ? 11 : 12} />
                          {ind.label}
                        </a>
                      );
                    })}
                  </div>

                  <div
                    style={{ height: 1, background: "#e2e8f0", marginBottom: 18 }}
                  />
                </>
              )}

              <p
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#94a3b8",
                  letterSpacing: "0.09em",
                  textTransform: "uppercase",
                  margin: "0 0 10px",
                }}
              >
                Follow Us
              </p>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {socialLinks.map((s, i) => {
                  const Icon = s.icon;
                  const isHov = hoveredSocial === i;
                  const hasValidUrl = s.url && s.url !== "#";
                  return (
                    <a
                      key={i}
                      href={s.url}
                      target={hasValidUrl ? "_blank" : undefined}
                      rel="noreferrer"
                      title={s.label}
                      className="kf-social-btn"
                      onMouseEnter={() => setHoveredSocial(i)}
                      onMouseLeave={() => setHoveredSocial(null)}
                      style={{
                        width: isMobile ? 36 : 38,
                        height: isMobile ? 36 : 38,
                        borderRadius: 10,
                        background: isHov ? s.color : s.bg,
                        border: "1.5px solid " + (isHov ? s.color : s.border),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: isHov ? "white" : s.color,
                        fontSize: isMobile ? 14 : 15,
                        textDecoration: "none",
                        boxShadow: isHov
                          ? "0 8px 20px " + s.color + "40"
                          : "none",
                        opacity: hasValidUrl ? 1 : 0.5,
                        cursor: hasValidUrl ? "pointer" : "default",
                      }}
                    >
                      <Icon />
                    </a>
                  );
                })}
              </div>
            </div>

            {/* NAV COLUMNS */}
            {navSections.map((sec, si) => (
              <div key={si} style={{ paddingTop: 4 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    marginBottom: 18,
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: sec.accent,
                      flexShrink: 0,
                      boxShadow: "0 0 6px " + sec.accent + "66",
                    }}
                  />
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 800,
                      color: "#0f172a",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                    }}
                  >
                    {sec.title}
                  </span>
                </div>
                <ul
                  style={{
                    listStyle: "none",
                    padding: 0,
                    margin: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  {sec.links.length > 0 ? (
                    sec.links.map((link, li) => {
                      const hoverKey = si + "-" + li;
                      const isHov = hoveredLink === hoverKey;
                      return (
                        <li key={li}>
                          <a
                            href={link.href}
                            onClick={(e) => {
                              if (link.document) {
                                e.preventDefault();
                                handlePolicyClick(link.document, e);
                              }
                            }}
                            className="kf-nav-link"
                            onMouseEnter={() => setHoveredLink(hoverKey)}
                            onMouseLeave={() => setHoveredLink(null)}
                            style={{
                              color: isHov ? sec.accent : "#64748b",
                              fontSize: isMobile ? "13px" : "13.5px",
                              textDecoration: "none",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 7,
                              fontWeight: 500,
                              paddingLeft: isHov ? 6 : 0,
                              cursor: "pointer",
                            }}
                          >
                            <span
                              style={{
                                width: 4,
                                height: 4,
                                borderRadius: "50%",
                                background: isHov ? sec.accent : "#cbd5e1",
                                flexShrink: 0,
                                transition: "background 0.18s",
                              }}
                            />
                            {link.name}
                            {isHov && (
                              <ArrowRight size={11} style={{ color: sec.accent }} />
                            )}
                          </a>
                        </li>
                      );
                    })
                  ) : (
                    <li>
                      <span
                        style={{
                          color: "#94a3b8",
                          fontSize: isMobile ? "12px" : "13px",
                          fontStyle: "italic",
                        }}
                      >
                        Legal documents are currently unavailable
                      </span>
                    </li>
                  )}
                </ul>
              </div>
            ))}
          </div>

          {/* CONTACT STRIP */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr"
                : isTablet
                  ? "repeat(2, 1fr)"
                  : "1fr 1fr 1fr",
            }}
          >
            {[
              {
                icon: <Mail size={isMobile ? 16 : 17} />,
                label: "Email",
                value: contactEmail,
                color: "#3b82f6",
                bg: "#eff6ff",
                border: "#bfdbfe",
                href: "mailto:" + contactEmail,
                external: false,
              },
              {
                icon: <Phone size={isMobile ? 16 : 17} />,
                label: "Phone",
                value: contactPhoneDisplay,
                color: "#8b5cf6",
                bg: "#f5f3ff",
                border: "#ddd6fe",
                href: "tel:" + contactPhone,
                external: false,
              },
              {
                icon: <MapPin size={isMobile ? 16 : 17} />,
                label: "Address",
                value: contactAddress,
                color: "#10b981",
                bg: "#ecfdf5",
                border: "#a7f3d0",
                href: "https://maps.google.com/?q=" + encodeURIComponent(contactAddress),
                external: true,
              },
            ].map((item, i) => (
              <a
                key={i}
                href={item.href}
                target={item.external ? "_blank" : undefined}
                rel="noreferrer"
                className="kf-contact-item"
                style={{
                  alignItems: "flex-start",
                  gap: isMobile ? 12 : 14,
                  padding: isMobile ? "16px" : "22px 28px",
                  borderRight:
                    isMobile
                      ? "none"
                      : i < 2 && isDesktop
                        ? "1.5px solid #e2e8f0"
                        : isTablet && i === 0
                          ? "1.5px solid #e2e8f0"
                          : "none",
                  borderBottom: "1.5px solid #e2e8f0",
                }}
              >
                <div
                  className="kf-cicon"
                  style={{
                    width: isMobile ? 38 : 42,
                    height: isMobile ? 38 : 42,
                    borderRadius: 11,
                    background: item.bg,
                    border: "1.5px solid " + item.border,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: item.color,
                    flexShrink: 0,
                  }}
                >
                  {item.icon}
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#94a3b8",
                      letterSpacing: "0.09em",
                      marginBottom: 4,
                    }}
                  >
                    {item.label}
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "13px" : "13.5px",
                      color: "#334155",
                      lineHeight: 1.55,
                      fontWeight: 500,
                      wordBreak: "break-word",
                    }}
                  >
                    {item.value}
                  </div>
                </div>
              </a>
            ))}
          </div>

          {/* BOTTOM BAR */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px 0",
              textAlign: "center",
            }}
          >
            {onMainPage ? (
              <p
                style={{
                  color: "#94a3b8",
                  fontSize: isMobile ? "12px" : "13px",
                  margin: 0,
                }}
              >
                {"© 2026 "}
                <a
                  href="https://www.avpstech.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#475569",
                    fontWeight: 700,
                    textDecoration: "none",
                    transition: "color 0.18s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "#1d4ed8")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "#475569")}
                >
                  AVPS Technologies Inc.
                </a>
              </p>
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  flexWrap: "wrap",
                  justifyContent: "center",
                }}
              >
                <span
                  style={{
                    color: "#94a3b8",
                    fontSize: isMobile ? "12px" : "13px",
                  }}
                >
                  Powered by
                </span>
                <a
                  href="https://www.krumago.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    textDecoration: "none",
                    opacity: 0.85,
                    transition: "opacity 0.18s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.85")}
                >
                  <img
                    src={KRUMAGO.IMAGES.KRUMAGO_3}
                    alt="Krumago"
                    style={{
                      height: isMobile ? "18px" : "20px",
                      width: "auto",
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default KrumagoFooter;
